// Dedicated public tick stream — completely independent of the trading socket.
// The chart, ticker and any other purely-public feature should use THIS, so
// they never break when the trading socket reconnects (e.g. after OAuth/OTP).

const WS_URL = "wss://api.derivws.com/trading/v1/options/ws/public";

let socket = null;
let opening = false;
let reqId = 1;
const pending = new Map();      // reqId -> { resolve, reject }
const subscribers = new Map();  // reqId -> handler
const subServerIds = new Map(); // reqId -> Deriv subscription.id (for server-side forget)
const disconnectHandlers = new Set(); // notified whenever the socket drops, so callers can resubscribe

// Register a callback fired whenever the public socket disconnects. Returns an
// unsubscribe fn. The chart uses this to re-subscribe and self-heal (a dropped
// socket silently kills live streams → black/frozen chart without this).
export const onPublicDisconnect = (cb) => {
  disconnectHandlers.add(cb);
  return () => disconnectHandlers.delete(cb);
};

const ensureSocket = () => {
  if (socket && socket.readyState === WebSocket.OPEN) return Promise.resolve(socket);
  if (opening) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const t = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          clearInterval(t);
          resolve(socket);
        } else if (Date.now() - start > 8000) {
          clearInterval(t);
          reject(new Error("public ws open timeout"));
        }
      }, 100);
    });
  }
  opening = true;
  return new Promise((resolve, reject) => {
    const s = new WebSocket(WS_URL);
    const t = setTimeout(() => {
      opening = false;
      try { s.close(); } catch { /* ignore */ }
      reject(new Error("public ws open timeout"));
    }, 8000);
    s.onopen = () => {
      clearTimeout(t);
      opening = false;
      socket = s;
      resolve(s);
    };
    s.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      const id = msg?.req_id;
      if (id != null && pending.has(id)) {
        const { resolve: r } = pending.get(id);
        pending.delete(id);
        r(msg);
      }
      if (id != null && subscribers.has(id)) {
        const handler = subscribers.get(id);
        if (typeof handler === "function") handler(msg);
      }
    };
    s.onclose = () => {
      socket = null;
      opening = false;
      // Fail any in-flight requests immediately (don't let awaits hang for 15s)
      // and drop the now-dead subscriptions so a fresh subscribe is clean.
      pending.forEach(({ reject: rej }) => { try { rej(new Error("public ws closed")); } catch { /* ignore */ } });
      pending.clear();
      subscribers.clear();
      subServerIds.clear();
      disconnectHandlers.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
    };
    s.onerror = () => {
      /* will be retried on next call */
    };
  });
};

export const publicSend = async (request, timeoutMs = 15000) => {
  await ensureSocket();
  const id = reqId++;
  const str = JSON.stringify({ ...request, req_id: id });
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error("public ws request timeout"));
    }, timeoutMs);
    pending.set(id, {
      resolve: (m) => {
        clearTimeout(timer);
        resolve(m);
      },
      reject,
    });
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(str);
    else reject(new Error("public ws not open"));
  });
};

export const publicSubscribe = async (request, handler) => {
  await ensureSocket();
  const id = reqId++;
  const str = JSON.stringify({ ...request, req_id: id, subscribe: 1 });
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error("public ws subscribe timeout"));
    }, 15000);
    pending.set(id, {
      resolve: (m) => {
        clearTimeout(timer);
        subscribers.set(id, handler);
        if (m?.subscription?.id) subServerIds.set(id, m.subscription.id);
        resolve({ subscription_id: id, first: m });
      },
      reject,
    });
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(str);
    else reject(new Error("public ws not open"));
  });
};

export const publicForget = (subscriptionId) => {
  if (subscribers.has(subscriptionId)) subscribers.delete(subscriptionId);
  // Deriv expects its OWN subscription.id (a string) for `forget` — NOT our
  // local req_id. Using the wrong id leaves the stream alive server-side, so
  // re-subscribing returns AlreadySubscribed (no candles) → blank chart on switch.
  const serverId = subServerIds.get(subscriptionId) || subscriptionId;
  subServerIds.delete(subscriptionId);
  if (serverId && socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ forget: serverId, req_id: reqId++ }));
    } catch {
      /* ignore */
    }
  }
};
